/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CaseService } from 'app/cm/services/case-service';
import { CustomCareTeamParticipant } from 'app/cm/components/case/care-team/care-team-grid';
import { I18N } from 'aurelia-i18n';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class CareTeamComments extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected caseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected careTeamParticipants: ReadonlyArray<CustomCareTeamParticipant> = [];

    protected careTeamComments: any[] = [];

    protected validateForm = validate;
    protected addCommentFormIsValid: boolean = false;
    protected userIsOnCareTeam: boolean = false;
    protected userCanAddComments: boolean = false;

    protected resultCount: number = 0;
    protected page: number = 0;
    protected pageSize: number = 20;
    protected allCommentsLoaded: boolean = true;
    protected showingMessage: string = '';

    protected showMoreVisible: boolean = false;
    protected showLessVisible: boolean = false;


    ////****************** Element References ***************************************////
    protected $addCommentForm!: IForm;
    protected $showingMessageElement!: HTMLElement | null;
    protected $showMoreButton!: HTMLElement | null;
    protected $showLessButton!: HTMLElement | null;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    // if user changes tab to another case, set page back to 0 and reload
    protected caseIdChanged(newValue: string, oldValue: string): void {
        if(newValue !== oldValue) {
            this.page = 0;
            this.userIsOnCareTeam = false;
            this.userCanAddComments = false;
            this.reload();
        }
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        this.checkUserIsOnCareTeam();
        await this.loadCareTeamComments();
    }

    public checkUserIsOnCareTeam(): void {
        // if user is on care team at all, user can see comments
        this.userIsOnCareTeam = this.careTeamParticipants.some(participant => this.env.user.id === participant.assigned?.toUser?.id);
        // but user can only add comments if user has an active participant relationship
        this.userCanAddComments = this.careTeamParticipants.some(participant => this.env.user.id === participant.assigned?.toUser?.id && participant.isActive);
    }

    protected initials(name: CM.PersonName): string {
        if(!name) return '';
        const results: string[] = [];
        if(name.first) { results.push(name.first.substr(0, 1)); }
        if(name.middle) { results.push(name.middle.substr(0, 1)); }
        if(name.last) { results.push(name.last.substr(0, 1)); }
        return results.join('');
    }

    protected formatMentions(mentions: CM.CareTeamParticipant[]): string | null {
        if(!mentions || mentions.length === 0) return null;
        const results: string[] = [];
        mentions.forEach(mention => {
            if(mention.assigned?.toUser?.name?.full) { results.push(mention.assigned.toUser.name.full); }
            if(mention.assigned?.toProvider?.name?.full) { results.push(mention.assigned.toProvider.name.full); }
        })
        return results.join(', ');
    }

    // "Showing n of x comments."
    protected updateShowingMessage(): void {
        this.showingMessage = this.i18n.tr('cm:careTeam.comment.showing.$', { current: this.careTeamComments.length, max: this.resultCount });
        this.updateShowButtons();
    }

    protected updateShowButtons(): void {
        const visible = this.careTeamComments.length;
        const total = this.resultCount;

        // Hide both if comments still fit below the page size
        if(total <= this.pageSize) {
            this.showMoreVisible = false;
            this.showLessVisible = false;
            return;
        }

        if(visible <= this.pageSize) {
            this.showMoreVisible = true;
            this.showLessVisible = false;
        }
        else if(visible < total) {
            this.showMoreVisible = true;
            this.showLessVisible = true;
        }
        else {
            this.showMoreVisible = false;
            this.showLessVisible = visible > this.pageSize;
        }
    }

    protected async showLessCareTeamComments(): Promise<void> {
        if(this.careTeamComments.length <= this.pageSize) return;

        const newVisibleCount = Math.max(this.pageSize, this.careTeamComments.length - this.pageSize);
        this.careTeamComments = this.careTeamComments.slice(0, newVisibleCount);

        // adjust highestLoadedPage and page to reflect removed page
        const pagesLoaded = Math.ceil(this.careTeamComments.length / this.pageSize);
        this.highestLoadedPage = Math.max(-1, pagesLoaded - 1);
        this.page = Math.max(0, pagesLoaded - 1);

        this.allCommentsLoaded = this.careTeamComments.length === this.resultCount;
        this.updateShowingMessage();

        if(this.$showLessButton && this.showLessVisible) this.$showLessButton.focus();
        else if(this.$showingMessageElement) this.$showingMessageElement.focus();
    }

    protected async handleAddCommentFormSuccess(): Promise<void> {
        if(this.$addCommentForm.reset) {
            this.$addCommentForm.reset();
        }
        this.page = 0; // reset page so new comment loads to top
        await this.reload();
        if(this.$showingMessageElement) this.$showingMessageElement.focus();
    }

    protected highestLoadedPage: number = -1;

    protected async loadMoreCareTeamComments(): Promise<void> {
        const currentVisible = this.careTeamComments.length;
        const total = this.resultCount || 0;

        // If we don't have a known total, fall back to the old behavior (increment page and reload)
        if(!total) {
            this.page++;
            await this.reload();
        } else {
            const targetVisible = Math.min(total, currentVisible + this.pageSize);

            if(!(currentVisible >= targetVisible)) {
                const startPage = Math.floor(currentVisible / this.pageSize);
                const endPage = Math.floor((targetVisible - 1) / this.pageSize);
                await this.loadPagesRange(startPage, endPage);
            }
        }

        if(this.$showMoreButton && this.showMoreVisible) this.$showMoreButton.focus();
        else if(this.$showingMessageElement) this.$showingMessageElement.focus();
    }

    // Load pages inclusive from startPage to endPage (0-based), append deduplicated results
    private async loadPagesRange(startPage: number, endPage: number): Promise<void> {
        if(!this.caseId) return;
        if(startPage > endPage) return;

        const includes: string[] = [
            'mentions:inactive',
            'mentions.assigned.toUser',
            'mentions.assigned.toProvider',
            'metadata.created.byUser'
        ];

        const fields: string[] = [
            'text',
            'mentions.assigned.toUser.name.full',
            'mentions.assigned.toProvider.name.full',
            'metadata.created.byUser.name.full',
            'metadata.created.timestamp',
            'metadata.created.byUser.description'
        ];

        try {
            this.state.beginLoad();

            for(let p = startPage; p <= endPage; p++) {
                const options: Partial<IQueryLoadOptions> = { top: this.pageSize, page: p };
                const response = await this.service.loadCaseCareTeamComments(this.caseId, options, includes, fields);
                const results = response.results || [];

                const existingIds = new Set(this.careTeamComments.map(c => c.id));
                for(const r of results) {
                    if(!existingIds.has(r.id)) {
                        this.careTeamComments.push(r);
                        existingIds.add(r.id);
                    }
                }

                this.resultCount = response.count || this.resultCount;
                this.highestLoadedPage = Math.max(this.highestLoadedPage, p);
            }

            this.allCommentsLoaded = this.careTeamComments.length === this.resultCount;
            this.updateShowingMessage();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadCareTeamComments(): Promise<void> {
        if(!this.caseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.page,
        };

        const includes: string[] = [
            'mentions:inactive',
            'mentions.assigned.toUser',
            'mentions.assigned.toProvider',
            'metadata.created.byUser'
        ];

        const fields: string[] = [
            'mentions.assigned.toUser.name.full',
            'mentions.assigned.toProvider.name.full',
            'metadata.created.byUser.description',
            'metadata.created.byUser.name.*',
            'metadata.created.timestamp',
            'text'
        ];

        try {
            this.state.beginLoad();
            const fields: string[] = [
                'text',
                'mentions.assigned.toUser.name.full',
                'mentions.assigned.toProvider.name.full',
                'metadata.created.byUser.name.full',
                'metadata.created.timestamp',
                'metadata.created.byUser.description'
            ];
            const response = await this.service.loadCaseCareTeamComments(this.caseId, options, includes, fields);
            if(this.page === 0) {
                this.careTeamComments = response.results;
            } else {
                this.careTeamComments.push(...response.results); // append new page's data
            }

            this.resultCount = response.count || 0;
            this.allCommentsLoaded = this.careTeamComments.length === this.resultCount;
            this.updateShowingMessage();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
